import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Header from "../components/Header";
import "../styles/MunicipalityMandateUsers.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faUserPen, faChartPie } from "@fortawesome/free-solid-svg-icons";
import api from '../api/axios';
import { storeTermImages, isTermPopulated } from '../cache/imageCache';
import UserAvatar from '../components/UserAvatar';

function MunicipalityMandateUsers() {
const { t } = useTranslation();
const { municipalityId, mandateId } = useParams();
const navigate = useNavigate();
const [users, setUsers] = useState([]);
const [loading, setLoading] = useState(true);

const mayor = users.find(u => u.role === "ROLE_MAYOR");
const president = users.find(u => u.role === "ROLE_PRESIDENT");
const regularUsers = users.filter(u => u.role !== "ROLE_PRESIDENT" && u.role !== "ROLE_MAYOR");

const [userData] = useState(() => {
  const storedUserInfo = localStorage.getItem("userInfo");
  return storedUserInfo ? JSON.parse(storedUserInfo) : {};
});
const [, setImagesReady] = useState(false);
const [municipalityImages, setMunicipalityImages] = useState({ logo: null, flag: null, name: null });

useEffect(() => {
  const cached = JSON.parse(localStorage.getItem('municipalities') || '[]');
  const found = cached.find(m => String(m.id) === String(municipalityId));
  if (found) {
    setMunicipalityImages({
      logo: found.logoImage ? `data:image/png;base64,${found.logoImage}` : null,
      flag: found.flagImage ? `data:image/png;base64,${found.flagImage}` : null,
      name: found.name || null,
    });
    return;
  }
  api.get(`/api/municipalities/${municipalityId}`)
    .then(res => {
      const d = res.data;
      setMunicipalityImages({
        logo: d.logoImage ? `data:image/png;base64,${d.logoImage}` : null,
        flag: d.flagImage ? `data:image/png;base64,${d.flagImage}` : null,
        name: d.name || null,
      });
    })
    .catch(() => {});
}, [municipalityId]);

// Fetch votable users using Axios
useEffect(() => {
  const fetchUsers = async () => {
    try {
      const response = await api.get(`/api/municipality-terms/${mandateId}/votable-users`);
      let data = response.data || [];

      // Sort users alphabetically by name using locale
      const locale = navigator.language || "en";
      data.sort((a, b) => a.name.localeCompare(b.name, locale, { sensitivity: "base" }));

      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchUsers();
}, [mandateId]);

useEffect(() => {
  if (!mandateId) return;
  if (isTermPopulated(mandateId)) { setImagesReady(true); return; }
  api.get(`/api/municipality-terms/${mandateId}/user-images`)
    .then(res => { storeTermImages(mandateId, res.data); setImagesReady(true); })
    .catch(() => setImagesReady(true));
}, [mandateId]);


  return (
    <div className="municipality-mandate-users-container">
      <HelmetProvider>
        <Helmet>
          <title>{t("MunicipalityMandateUsers.title")}</title>
        </Helmet>
      </HelmetProvider>

      <Header isSticky={true} />

      <main className="municipality-mandate-users-content">

        {/* Header */}
        <div className="municipality-mandate-users-header">
          <div className="header-section">
            <button className="back-button" onClick={() => navigate(-1)}>
                <span className="back-icon">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </span>
                <span className="back-text">
                    {t("common.back")}
                </span>
            </button>
          </div>

          <div className="header-section title-section">
            <h1 className="municipality-mandate-users-title">
              {t("MunicipalityMandateUsers.title")}
            </h1>
          </div>

          <div className="header-section">
             {userData.role === 'ROLE_ADMIN' && (
                  <button
                    className="municipalaty-mandate-users-view-button"
                    onClick={() => navigate(`/municipalities/${municipalityId}/mandates/users/${mandateId}/add-list`)}
                  >
                    {t('common.edit')} <FontAwesomeIcon icon={faUserPen} />
                  </button>
                )}
          </div>
        </div>

        {/* Flag, Municipality Name & Coat of Arms banner */}
        {(municipalityImages.flag || municipalityImages.logo || municipalityImages.name) && (
          <div className="mmu-emblem-banner">
            {municipalityImages.flag && (
              <img src={municipalityImages.flag} alt="flag" className="mmu-flag-img" />
            )}
            {municipalityImages.name && (
              <span className="mmu-municipality-name">{municipalityImages.name}</span>
            )}
            {municipalityImages.logo && (
              <img src={municipalityImages.logo} alt="coat of arms" className="mmu-logo-img" />
            )}
          </div>
        )}

        <div className="mmu-statistics-button-wrap">
          <button
            className="mmu-statistics-button"
            onClick={() => navigate(`/municipalities/${municipalityId}/mandates/users/${mandateId}/statistics`)}
          >
            {t('MunicipalityMandateUsers.statisticsButton')} <FontAwesomeIcon icon={faChartPie} />
          </button>
        </div>

        {/* Spinner */}
        {loading && (
          <div className="municipalaty-mandate-user-spinner">
            <img
              src={`${process.env.PUBLIC_URL}/images/loading.svg`}
              alt={t("MunicipalityMandateUsers.loading")}
            />
          </div>
        )}

        {!loading && (mayor || president) && (
  <div className="municipality-mandate-leadership-bar">
    {mayor && (
      <div className="municipality-mandate-leadership-half mayor"
        onClick={() => navigate(`/profile-view/${mayor.username}`)}>
        <UserAvatar username={mayor.username} name={mayor.name} surname={mayor.surname} className="municipality-mandate-users-avatar" termId={mandateId} />
        <p className="municipality-mandate-users-name">
          {mayor.name} {mayor.surname}
        </p>
        <span className="municipality-mandate-mayor-label">
          {t("MunicipalityMandateUsers.mayor")}
        </span>
      </div>
    )}
    {mayor && president && <div className="municipality-mandate-leadership-divider" />}
    {president && (
      <div className="municipality-mandate-leadership-half president"
        onClick={() => navigate(`/profile-view/${president.username}`)}>
        <UserAvatar username={president.username} name={president.name} surname={president.surname} className="municipality-mandate-users-avatar" termId={mandateId} />
        <p className="municipality-mandate-users-name">
          {president.name} {president.surname}
        </p>
        <span className="municipality-mandate-president-label">
          {t("MunicipalityMandateUsers.president")}
        </span>
      </div>
    )}
  </div>
)}

        {/* Users grid */}
       {!loading && regularUsers.length > 0 && (
  <div className="municipality-mandate-users-grid">
    {regularUsers.map((user) => (
      <div
          key={user.username}
          className="municipality-mandate-users-card"
          onClick={() => navigate(`/profile-view/${user.username}`)}
      >
        <UserAvatar username={user.username} name={user.name} surname={user.surname} className="municipality-mandate-users-avatar" termId={mandateId} />
        <p className="municipality-mandate-users-name">
          {user.name} {user.surname}
        </p>
      </div>
    ))}
  </div>
)}

      </main>
    </div>
  );
}

export default MunicipalityMandateUsers;
